import React from "react"
import {
  styled,
  forwardRef,
  createStyles,
  NamedStyle,
  GenericStyleProp,
} from "@design-system/core"
import { __DEV__ } from "@design-system/utils"
import { useInteractive } from "@design-system/interactive"

import { ButtonProps, ButtonVariants } from "./types"

export const Button = forwardRef<ButtonProps, "button">((props, ref) => {
  const {
    size,
    icon,
    iconPosition,
    children,
    style,
    iconStyle,
    variant,
    isFullWidth,
    ...otherProps
  } = props
  const {
    isDisabled,
    isHovered,
    isPressed,
    isFocused,
    ...interactiveProps
  } = useInteractive(otherProps)
  const variantStyle = variant ? variantStyles[variant] : undefined

  const iconElement = icon
    ? React.cloneElement(React.Children.only(icon), {
        style: [
          styles.icon,
          iconPosition === "right" ? styles.iconRight : styles.iconLeft,
          variantStyle?.default.icon,
          isDisabled && [
            stateStyles.disabled.icon,
            variantStyle?.states.disabled.icon,
          ],
          isHovered && [
            stateStyles.hover.icon,
            variantStyle?.states.hover.icon,
          ],
          size && [sizeStyles[size]?.icon, variantStyle?.sizes[size]?.icon],
          icon.props?.style,
          iconStyle,
        ],
      })
    : null

  return (
    <styled.button
      {...interactiveProps}
      ref={ref}
      disabled={isDisabled}
      style={
        [
          styles.container,
          isFullWidth && styles.fullWidth,
          variantStyle?.default.container,
          size && [
            sizeStyles[size]?.container,
            variantStyle?.sizes[size]?.container,
          ],
          isDisabled && [
            styles.disabled,
            stateStyles.disabled.container,
            variantStyle?.states.disabled.container,
          ],
          isHovered && [
            stateStyles.hover.container,
            variantStyle?.states.hover.container,
          ],
          style,
        ] as GenericStyleProp<NamedStyle>
      }
    >
      {iconPosition === "right" ? null : iconElement}
      {children}
      {iconPosition === "right" ? iconElement : null}
    </styled.button>
  )
})

Button.defaultProps = {
  variant: "default",
}

if (__DEV__) {
  Button.displayName = "Button"
}

const styles = createStyles((theme) => ({
  container: {
    alignItems: "center",
    justifyContent: "center",
    display: "inline-flex",
    flexDirection: "row",
    cursor: "pointer",
    whiteSpace: "nowrap",
    userSelect: "none",
    borderStyle: "solid",
    borderColor: theme.colors.transparent,
    padding: 0,
    ...theme.button.base.default.container,
  },
  disabled: {
    cursor: "default",
  },
  icon: theme.button.base.default.icon,
  iconLeft: {
    marginLeft: 0,
  },
  iconRight: {
    marginRight: 0,
  },
  fullWidth: {
    width: "100%",
  },
}))

const sizeStyles = {
  small: createStyles((theme) => theme.button.base.sizes.small || {}),
  medium: createStyles((theme) => theme.button.base.sizes.medium || {}),
  large: createStyles((theme) => theme.button.base.sizes.large || {}),
}

const stateStyles = {
  hover: createStyles((theme) => theme.button.base.states.hover || {}),
  disabled: createStyles((theme) => theme.button.base.states.disabled || {}),
}

const createVariantStyles = (name: ButtonVariants) => ({
  default: createStyles((theme) => theme.button.variants[name].default),
  sizes: {
    small: createStyles(
      (theme) => theme.button.variants[name].sizes.small || {},
    ),
    medium: createStyles(
      (theme) => theme.button.variants[name].sizes.medium || {},
    ),
    large: createStyles(
      (theme) => theme.button.variants[name].sizes.large || {},
    ),
  },
  states: {
    hover: createStyles(
      (theme) => theme.button.variants[name].states.hover || {},
    ),
    disabled: createStyles(
      (theme) => theme.button.variants[name].states.disabled || {},
    ),
  },
})

const variantStyles = {
  default: createVariantStyles("default"),
  cta: createVariantStyles("cta"),
  primary: createVariantStyles("primary"),
  secondary: createVariantStyles("secondary"),
  inverse: createVariantStyles("inverse"),
}
